import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { queryKeys, queryKeyUtils } from "../lib/queryKeys";

// Query hooks
export const useTransactions = (params = {}) => {
  return useQuery({
    queryKey: queryKeys.transactions.list(params),
    queryFn: () => api.getTransactions(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (previousData) => previousData, // Keep previous data while new data is loading
  });
};;;

export const useFinancialSummary = (params = {}) => {
  return useQuery({
    queryKey: queryKeys.financial.summary(params),
    queryFn: () => api.getFinancialSummary(params),
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData, // Keep previous data while new data is loading
  });
};;;

export const useFinancialAdvisor = (params = {}) => {
  return useQuery({
    queryKey: queryKeys.financial.advisor(params),
    queryFn: () => api.getFinancialAdvisor(params),
    staleTime: 10 * 60 * 1000, // AI analysis can be cached longer
    placeholderData: (previousData) => previousData,
  });
};;

export const useCategorySpending = (params = {}) => {
  return useQuery({
    queryKey: queryKeys.categories.spending(params),
    queryFn: () => api.getCategorySpending(params),
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData, // Keep previous data while new data is loading
    // Ensure we always return an array, never undefined
    select: (data) => Array.isArray(data) ? data : [],
  });
};;;

export const useSubCategorySpending = (params = {}) => {
  return useQuery({
    queryKey: queryKeys.categories.subSpending(params),
    queryFn: () => api.getSubCategorySpending(params),
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData, // Keep previous data while new data is loading
  });
};;;

export const useCategoryHistoricalAverage = (params = {}) => {
  return useQuery({
    queryKey: queryKeys.categories.historicalAverage(params),
    queryFn: () => api.getCategoryHistoricalAverage(params),
    staleTime: 10 * 60 * 1000, // 10 minutes - longer since this is historical data
    placeholderData: (previousData) => previousData, // Keep previous data while new data is loading
  });
};;;

// Keep old hook for backward compatibility
export const useCategory90DayAverage = (params = {}) => {
  return useQuery({
    queryKey: queryKeys.categories.ninetyDayAverage(params),
    queryFn: () => api.getCategory90DayAverage(params),
    staleTime: 10 * 60 * 1000, // 10 minutes - longer since this is historical data
    placeholderData: (previousData) => previousData, // Keep previous data while new data is loading
  });
};;;

export const useDailySpending = (params = {}) => {
  return useQuery({
    queryKey: queryKeys.dailySpending(params),
    queryFn: () => api.getDailySpending(params),
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData, // Keep previous data while new data is loading
  });
};;;

export const useBudgetComparison = (params = {}) => {
  return useQuery({
    queryKey: queryKeys.budget.comparison(params),
    queryFn: () => api.getBudgetComparison(params),
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData, // Keep previous data while new data is loading
  });
};;;

export const useCategories = () => {
  return useQuery({
    queryKey: queryKeys.categories.all,
    queryFn: api.getCategories,
    staleTime: 10 * 60 * 1000, // Categories change less frequently
  });
};

export const useKeywords = () => {
  return useQuery({
    queryKey: queryKeys.keywords.all,
    queryFn: api.getKeywords,
    staleTime: 10 * 60 * 1000,
  });
};

export const useMonthlyExpenses = () => {
  return useQuery({
    queryKey: queryKeys.monthlyExpenses.all,
    queryFn: api.getMonthlyExpenses,
    staleTime: 10 * 60 * 1000,
  });
};

// DEPRECATED: Upload hook redirected to safe append for backwards compatibility
export const useUploadTransactions = () => {
  console.warn("⚠️  DEPRECATED: useUploadTransactions redirected to useAppendTransactions for safety");
  console.warn("📧 Please update your code to use useAppendTransactions() directly");
  return useAppendTransactions();
};

export const useAppendTransactions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input) => {
      // Support both old format (just transactions) and new format (with options)
      if (Array.isArray(input)) {
        // Old format: just transactions array
        return api.appendTransactions(input);
      } else {
        // New format: { transactions, options }
        return api.appendTransactions(input.transactions, input.options);
      }
    },
    onSuccess: () => {
      // Invalidate all transaction-related queries
      queryKeyUtils.getTransactionRelatedKeys().forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    },
  });
};
export const useAddTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.addTransaction,
    onSuccess: () => {
      // Invalidate all transaction‑related queries
      queryKeyUtils.getTransactionRelatedKeys().forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    },
  });
};

export const useUpdateTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.updateSingleTransaction,
    onMutate: async (updated) => {
      await queryClient.cancelQueries({ queryKey: ["transactions", "list"] });
      const snapshots = queryClient.getQueriesData({
        queryKey: ["transactions", "list"],
      });

      queryClient.setQueriesData({ queryKey: ["transactions", "list"] }, (current) => {
        if (!Array.isArray(current)) return current;
        return current.map((transaction) =>
          transaction?.id === updated.id
            ? { ...transaction, ...updated }
            : transaction,
        );
      });

      return { snapshots };
    },
    onError: (_error, _updated, context) => {
      context?.snapshots?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSuccess: () => {
      // The edited row is already in the list cache. Refresh aggregates in the
      // background without reloading the transaction list or historical charts.
      queryClient.invalidateQueries({
        queryKey: ["financial"],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: ["categories", "spending"],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: ["daily-spending"],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: ["budget"],
        refetchType: "active",
      });
    },
  });
};

export const useAddKeyword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ keyword, category, person }) => api.addKeyword(keyword, category, person),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["keywords"] });
      // Don't need to invalidate everything - keywords only affect categorization during import
    },
  });
};

export const useUpdateKeyword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, keyword, category, person }) =>
      api.updateKeyword(id, keyword, category, person),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["keywords"] });
    },
  });
};

export const useDeleteKeyword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => api.deleteKeyword(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["keywords"] });
    },
  });
};

// Monthly Expenses individual operations
export const useAddMonthlyExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, amount, category }) => api.addMonthlyExpense(name, amount, category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["monthly-expenses"] });
      queryClient.invalidateQueries({ queryKey: ["financial"] });
      queryClient.invalidateQueries({ queryKey: ["budget"] });
    },
  });
};

export const useUpdateMonthlyExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name, amount, category }) => api.updateMonthlyExpense(id, name, amount, category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["monthly-expenses"] });
      queryClient.invalidateQueries({ queryKey: ["financial"] });
      queryClient.invalidateQueries({ queryKey: ["budget"] });
    },
  });
};

export const useDeleteMonthlyExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => api.deleteMonthlyExpense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["monthly-expenses"] });
      queryClient.invalidateQueries({ queryKey: ["financial"] });
      queryClient.invalidateQueries({ queryKey: ["budget"] });
    },
  });
};

// Monthly Income individual operations
export const useAddMonthlyIncome = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, amount, category }) => api.addMonthlyIncome(name, amount, category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["monthly-expenses"] }); // Also contains income
      queryClient.invalidateQueries({ queryKey: ["financial"] });
      queryClient.invalidateQueries({ queryKey: ["budget"] });
    },
  });
};

export const useUpdateMonthlyIncome = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name, amount, category }) => api.updateMonthlyIncome(id, name, amount, category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["monthly-expenses"] });
      queryClient.invalidateQueries({ queryKey: ["financial"] });
      queryClient.invalidateQueries({ queryKey: ["budget"] });
    },
  });
};

export const useDeleteMonthlyIncome = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => api.deleteMonthlyIncome(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["monthly-expenses"] });
      queryClient.invalidateQueries({ queryKey: ["financial"] });
      queryClient.invalidateQueries({ queryKey: ["budget"] });
    },
  });
};

// Monthly Budget individual operations
export const useAddMonthlyBudget = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ category, amount }) => api.addMonthlyBudget(category, amount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["monthly-expenses"] });
      queryClient.invalidateQueries({ queryKey: ["financial"] });
      queryClient.invalidateQueries({ queryKey: ["budget"] });
    },
  });
};

export const useUpdateMonthlyBudget = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, category, amount }) => api.updateMonthlyBudget(id, category, amount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["monthly-expenses"] });
      queryClient.invalidateQueries({ queryKey: ["financial"] });
      queryClient.invalidateQueries({ queryKey: ["budget"] });
    },
  });
};

export const useDeleteMonthlyBudget = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => api.deleteMonthlyBudget(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["monthly-expenses"] });
      queryClient.invalidateQueries({ queryKey: ["financial"] });
      queryClient.invalidateQueries({ queryKey: ["budget"] });
    },
  });
};

export const useSaveKeywords = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.saveKeywords,
    onSuccess: () => {
      // Comprehensive invalidation for keywords since they affect categorization
      // Invalidate all queries to ensure everything refreshes
      queryClient.invalidateQueries();
      
      // Also specifically invalidate the main data patterns
      queryClient.invalidateQueries({ queryKey: ["financial"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["daily-spending"] });
      queryClient.invalidateQueries({ queryKey: ["budget"] });
      queryClient.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
};

export const useSaveMonthlyExpenses = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.saveMonthlyExpenses,
    onSuccess: () => {
      // Comprehensive invalidation for monthly expenses since they affect budget calculations
      // Invalidate all queries to ensure everything refreshes
      queryClient.invalidateQueries();
      
      // Also specifically invalidate the main data patterns
      queryClient.invalidateQueries({ queryKey: ["financial"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["daily-spending"] });
      queryClient.invalidateQueries({ queryKey: ["budget"] });
    },
  });
};

export const useUploadKeywords = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.uploadKeywords,
    onSuccess: () => {
      // Comprehensive invalidation for keywords since they affect categorization
      queryClient.invalidateQueries();
    },
  });
};

export const useRecategorizeTransactions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.recategorizeTransactions,
    onSuccess: () => {
      // This affects most data, so invalidate broadly
      queryClient.invalidateQueries({ queryKey: queryKeys.transactions.all });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["financial"] });
      queryClient.invalidateQueries({ queryKey: ["daily-spending"] });
      queryClient.invalidateQueries({ queryKey: ["budget"] });
    },
  });
};

export const useDeleteTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.deleteTransaction,
    onMutate: async (transactionId) => {
      await queryClient.cancelQueries({ queryKey: ["transactions", "list"] });
      const snapshots = queryClient.getQueriesData({
        queryKey: ["transactions", "list"],
      });
      queryClient.setQueriesData({ queryKey: ["transactions", "list"] }, (current) => {
        if (!Array.isArray(current)) return current;
        return current.filter((transaction) => transaction?.id !== transactionId);
      });
      return { snapshots };
    },
    onError: (_error, _transactionId, context) => {
      context?.snapshots?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["financial"],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: ["categories", "spending"],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: ["daily-spending"],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: ["budget"],
        refetchType: "active",
      });
    },
  });
};

// Utility hook for invalidating all data (useful for force refresh)
export const useInvalidateAll = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries();
  };
};

// Utility hook for comprehensive data refresh when core data changes
export const useRefreshAllData = () => {
  const queryClient = useQueryClient();

  return () => {
    // Invalidate all data-related queries comprehensively
    queryKeyUtils.getAllDataRelatedKeys().forEach((key) => {
      queryClient.invalidateQueries({ queryKey: key });
    });
    
    // Also invalidate by prefix patterns to catch parameterized queries
    queryClient.invalidateQueries({ queryKey: ["financial"] });
    queryClient.invalidateQueries({ queryKey: ["categories"] });
    queryClient.invalidateQueries({ queryKey: ["daily-spending"] });
    queryClient.invalidateQueries({ queryKey: ["budget"] });
    queryClient.invalidateQueries({ queryKey: ["transactions"] });
  };
};

// Query hook for transaction history
export const useTransactionHistory = (transactionId) => {
  return useQuery({
    queryKey: queryKeys.transactions.history(transactionId),
    queryFn: () => api.getTransactionHistory(transactionId),
    enabled: !!transactionId, // Only run if transactionId is provided
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};
